import { useState } from "react";
import { ArrowLeft, MessageSquare, Phone, User } from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";
import { ConfidenceBadge } from "@vseh/ai-components";

import { customersApi, type CustomerDto } from "../shared/api";
import { useFetch } from "../shared/hooks";
import { EmptyBlock, ErrorBlock } from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";
import { useT } from "../shared/i18n";


export function CustomersPage(): JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null);
  if (activeId) {
    return <CustomerDetail customerId={activeId} onBack={() => setActiveId(null)} />;
  }
  return <CustomersList onOpen={setActiveId} />;
}


function CustomersList({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const t = useT();
  const { data, error, loading, refetch } = useFetch<CustomerDto[]>(
    (signal) => customersApi.list(50, { signal }),
    [],
  );

  return (
    <Card>
      <CardHeader
        title={t("customers.page.title")}
        description={data ? String(data.length) : undefined}
        actions={<Button variant="ghost" size="xs" onClick={refetch}>{t("common.refresh")}</Button>}
      />

      {loading && !data && <SkeletonTable rows={6} cols={4} />}
      {error && <ErrorBlock error={error} onRetry={refetch} />}
      {data && data.length === 0 && (
        <EmptyBlock
          title={t("customers.empty.title")}
          description={t("customers.empty.description")}
        />
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-base-100">
          {data.map((c) => (
            <li
              key={c.id}
              className="flex cursor-pointer items-center gap-3 py-2.5 hover:bg-base-50"
              onClick={() => onOpen(c.id)}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-100 text-primary-800">
                <User aria-hidden="true" className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-base-900">{c.display_name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-2xs text-base-500">
                  {c.telegram_user_id && (
                    <span className="font-mono">tg:{c.telegram_user_id}</span>
                  )}
                  {c.phone && <span><Phone className="mr-1 inline size-3" />{c.phone}</span>}
                  {c.email && <span>{c.email}</span>}
                  <span>{new Date(c.created_at).toLocaleDateString("ru-RU")}</span>
                </div>
              </div>
              {c.trust_score != null && (
                <ConfidenceBadge value={c.trust_score} source="trust" />
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


function CustomerDetail({
  customerId, onBack,
}: { customerId: string; onBack: () => void }): JSX.Element {
  const t = useT();
  const { data, error, loading, refetch } = useFetch<CustomerDto>(
    (signal) => customersApi.get(customerId, { signal }),
    [customerId],
  );

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="size-4" />} onClick={onBack}>
          {t("customers.back")}
        </Button>
      </div>

      <Card>
        {loading && !data && <SkeletonTable rows={6} cols={4} />}
        {error && <ErrorBlock error={error} onRetry={refetch} />}
        {data && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl font-semibold text-base-900">
                  {data.display_name}
                </h2>
                {data.company_name && (
                  <div className="text-sm text-base-600">{data.company_name}</div>
                )}
              </div>
              {data.trust_score != null && (
                <ConfidenceBadge value={data.trust_score} source="trust score" size="md" />
              )}
            </div>

            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-sm">
              <KV label="ID"            value={<code className="font-mono text-xs">{data.id}</code>} />
              <KV label="Phone"         value={data.phone ?? "—"} />
              <KV label="Email"         value={data.email ?? "—"} />
              <KV label="Telegram ID"   value={data.telegram_user_id != null ? String(data.telegram_user_id) : "—"} />
              <KV label={t("customers.kv.created")} value={new Date(data.created_at).toLocaleString("ru-RU")} />
            </dl>

            <div className="rounded-md border border-ai-200 bg-ai-50 p-3 text-sm text-ai-900">
              <div className="flex items-center gap-2 text-2xs font-medium uppercase tracking-wide">
                <MessageSquare className="size-3" /> {t("customers.related")}
              </div>
              <p className="mt-1 text-base-700">
                Conversations / Orders / Deals view will land in Sprint 6.{" "}
                {t("customers.related.note")}
              </p>
            </div>

            <Badge tone="ai" dot>provenance: vseh-os</Badge>
          </div>
        )}
      </Card>
    </div>
  );
}


function KV({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <>
      <dt className="text-base-500">{label}</dt>
      <dd className="text-base-900">{value}</dd>
    </>
  );
}
